import { createFeatureSelector, createSelector } from '@ngrx/store'
import { InitializeState } from '../state/initial.state'
import { FeedbackDetail } from '../../models/api.model'
import { GlobalConstants } from '../../constants/global-constants'

const getInitializeState = createFeatureSelector<InitializeState>('initialize')

export const getTaskTypesFromState = createSelector(getInitializeState, (state) => state.taskTypes) // caution : will return all task types (creatableByEmployee flag is not filtered)

export const getFilteredTaskTypesByModuleAndCategoryAndSubCategoryFromState = (
  module: string,
  category: string,
  subCategory: string
) =>
  createSelector(getTaskTypesFromState, (state) => {
    const taskTypes =
      state && state[category] && state[category][subCategory] && state[category][subCategory][module]
        ? state[category][subCategory][module]
        : null
    return taskTypes ? taskTypes.filter((task) => task?.creatableByEmployee === true) : null // Filter the task types based on the creatableByEmployee flag
  })

export const getUserNameFromState = createSelector(getInitializeState, (state) => state?.user?.firstName)
export const getUserFromState = createSelector(getInitializeState, (state) => state?.user)

export const getUIStatusMappingFromState = createSelector(getInitializeState, (state) => state?.uiStatusMapping)

export const getGroupIdAndNotificationPrefFromUserState = createSelector(getInitializeState, (state) => {
  const user = state?.user
  return {
    groupId: user?.companyGroupId,
    notificationPreferences: user?.notificationPreferences
  }
})

export const getCompanySettingsFromState = createSelector(getInitializeState, (state) => state?.companySettings)

export const getEnabledModules = createSelector(
  getCompanySettingsFromState,
  (settings) => settings?.modules ?? []
)

export const getFeedbackDetailFromState = (id: string) =>
  createSelector(
    selectFeedbackListFromState,
    (feedbackList: FeedbackDetail[]) => feedbackList?.find((feedback: FeedbackDetail) => feedback.id === id) ?? null
  )

export const selectFeedbackListFromState = createSelector(getInitializeState, (state) => state?.feedbackList)

export const getNextUnInteractedFeedbackId = (currentFeedbackId: string) =>
  createSelector(selectFeedbackListFromState, (state) => {
    const feedbackList: FeedbackDetail[] = state
    if (!feedbackList || !currentFeedbackId) {
      return null
    }
    const unInteractedFeedback = feedbackList.find(
      (feedback) =>
        feedback.uiStatus === GlobalConstants.FEEDBACK_UI_STATUS.NOT_INTERACTED && feedback.id !== currentFeedbackId
    )
    return unInteractedFeedback ? unInteractedFeedback.id : null
  })

export const getTaskCategorySubCategoryMapFromState = (module: string) =>
  createSelector(getTaskTypesFromState, (state) => {
    const taskTypes = state
    const categorySubcategoryMap = new Map<string, string[]>()

    if (taskTypes) {
      for (let [category, subCategoryData] of Object.entries(taskTypes)) {
        let subCategoryArr: string[] = []
        for (const subCategory of Object.keys(subCategoryData)) {
          if (module != 'NA') {
            const moduleData = subCategoryData[subCategory]
            if (moduleData.hasOwnProperty(module)) {
              subCategoryArr.push(subCategory)
            }
          } else {
            //custom categorization for knowledge article
            // Labour and Other LLC reduced to single category as LLC
            category = category.replace(GlobalConstants.CATEGORY_TYPE.OTHER_LLC, GlobalConstants.CATEGORY_TYPE.LLC)
            if (subCategory) {
              category =
                subCategory === GlobalConstants.CATEGORY_TYPE.LABOUR ? GlobalConstants.CATEGORY_TYPE.LLC : category
            }
            if (!subCategoryArr.includes(subCategory)) {
              subCategoryArr.push(subCategory)
            }
            if (category === GlobalConstants.CATEGORY_TYPE.LLC) {
              let existingLLCSubCategories = categorySubcategoryMap.get(category) || []
              const combinedSubCategories = new Set([...subCategoryArr, ...existingLLCSubCategories])
              subCategoryArr = Array.from(combinedSubCategories)
            }
          }
        }
        if (subCategoryArr.length > 0) {
          categorySubcategoryMap.set(category, subCategoryArr)
        }
      }
    }
    return categorySubcategoryMap
  })

export const getStatusListByCodeFromState = (
  categoryName: string,
  subCategoryName: string,
  moduleName: string,
  code: string
) =>
  createSelector(getTaskTypesFromState, (state) => {
    return (
      (
        state &&
        state[categoryName] &&
        state[categoryName][subCategoryName] &&
        state[categoryName][subCategoryName][moduleName] &&
        state[categoryName][subCategoryName][moduleName]
      )?.find((task) => task.code === code)?.statusList ?? null
    )
  })

export const getScreenConfigFromState = (type: string) =>
  createSelector(getInitializeState, (state) => state?.screenConfigs?.find((s) => s.type === type))
